import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    House,
    CalendarBlank,
    Receipt,
    AirplaneTilt,
    ChartBar,
    UsersThree,
    Buildings,
    Briefcase,
    SignOut,
    ShieldCheck,
    Database,
    GridFour,
    BookOpen,
    Gear,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const employeeNav = [
    { to: "/", label: "Resumen", icon: House, end: true },
    { to: "/timesheet", label: "Parte de horas", icon: CalendarBlank },
    { to: "/expenses", label: "Gastos y dietas", icon: Receipt },
    { to: "/absences", label: "Vacaciones y ausencias", icon: AirplaneTilt },
    { to: "/reports", label: "Informes", icon: ChartBar },
    { to: "/manual", label: "Manual de usuario", icon: BookOpen },
];

const adminNav = [
    { to: "/admin/approvals", label: "Aprobaciones", icon: ShieldCheck, role: "pm_or_admin" },
    { to: "/admin/invoices", label: "Facturas emitidas", icon: Receipt, role: "pm_or_admin" },
    { to: "/admin/users", label: "Usuarios", icon: UsersThree, role: "admin" },
    { to: "/admin/customers", label: "Clientes", icon: Buildings, role: "admin" },
    { to: "/admin/projects", label: "Proyectos", icon: Briefcase, role: "admin" },
    { to: "/admin/expenses", label: "Gastos y dietas", icon: Receipt, role: "admin" },
    { to: "/admin/supplier-invoices", label: "Facturas soportadas", icon: Receipt, role: "admin" },
    { to: "/admin/absences-matrix", label: "Matriz vacaciones", icon: GridFour, role: "admin" },
    { to: "/admin/data", label: "Datos", icon: Database, role: "admin" },
    { to: "/admin/settings", label: "Ajustes", icon: Gear, role: "admin" },
];

export default function Layout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    if (!user) return null;
    const isAdmin = user.role === "admin";
    const isPm = user.role === "pm";
    const visibleAdmin = adminNav.filter((it) =>
        it.role === "admin" ? isAdmin : isAdmin || isPm,
    );
    const hasAdminSection = visibleAdmin.length > 0;

    return (
        <div className="min-h-screen flex bg-[#F8F9FA]">
            <aside
                className="w-64 bg-slate-950 text-slate-100 flex flex-col sticky top-0 h-screen"
                data-testid="sidebar"
            >
                <div
                    className="px-5 py-6 border-b border-slate-800 cursor-pointer"
                    onClick={() => navigate("/")}
                    data-testid="brand-logo"
                >
                    <div className="font-display text-2xl font-bold tracking-tight">
                        Ingartek
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">
                        Portal
                    </div>
                </div>

                <nav className="py-3 flex-1 overflow-y-auto">
                    <div className="px-4 pb-2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        Mi espacio
                    </div>
                    {employeeNav.map((it) => (
                        <NavLink
                            key={it.to}
                            to={it.to}
                            end={it.end}
                            className={({ isActive }) =>
                                `nav-item ${isActive ? "is-active" : ""}`
                            }
                            data-testid={`nav-${it.label.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                            <it.icon size={18} weight="regular" />
                            <span>{it.label}</span>
                        </NavLink>
                    ))}

                    {hasAdminSection && (
                        <>
                            <div className="px-4 pt-5 pb-2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                Administración
                            </div>
                            {visibleAdmin.map((it) => (
                                <NavLink
                                    key={it.to}
                                    to={it.to}
                                    className={({ isActive }) =>
                                        `nav-item ${isActive ? "is-active" : ""}`
                                    }
                                    data-testid={`nav-${it.label.replace(/\s+/g, "-").toLowerCase()}`}
                                >
                                    <it.icon size={18} weight="regular" />
                                    <span>{it.label}</span>
                                </NavLink>
                            ))}
                        </>
                    )}
                </nav>

                <div className="border-t border-slate-800 px-4 py-4 flex items-center gap-3">
                    {user.picture ? (
                        <img
                            src={user.picture}
                            alt={user.name}
                            className="w-9 h-9 object-cover"
                            data-testid="user-avatar"
                        />
                    ) : (
                        <div className="w-9 h-9 bg-slate-800 flex items-center justify-center text-sm font-semibold">
                            {user.name?.[0]?.toUpperCase() || "?"}
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <div
                            className="text-sm font-medium truncate"
                            data-testid="user-name"
                        >
                            {user.name}
                        </div>
                        <div className="text-[11px] text-slate-400 truncate">
                            {user.role === "admin"
                                ? "Administrador"
                                : user.role === "pm"
                                  ? "Project Manager"
                                  : "Empleado"}{" "}
                            · {user.email}
                        </div>
                    </div>
                    <button
                        onClick={logout}
                        title="Cerrar sesión"
                        className="text-slate-400 hover:text-white p-2"
                        data-testid="logout-btn"
                    >
                        <SignOut size={18} />
                    </button>
                </div>
            </aside>

            <main className="flex-1 min-w-0">
                <Outlet />
            </main>
        </div>
    );
}
